/**
 * Menu View — Coordinator for the "Order Food" mode.
 * Manages cart state, dietary filters, active category, and scroll-spy.
 * Cart is backed up to sessionStorage on every change.
 */

import { getCurrentLanguage, t } from '../../i18n';
import { getRestaurantMenu, submitRestaurantOrder, fetchRestaurantOrders, getHotelInfo, type SubmitRestaurantOrderPayload } from '../../api';
import { initScrollSpy, destroy as destroyScrollSpy } from './scroll-spy';
import { renderDietFilter, updateDietChipUI } from './components/diet-filter';
import { renderCategoryTabs, setActiveTabUI, handleCatTabClick, itemMatchesFilters } from './components/category-tabs';
import { renderMenuSection } from './components/menu-section';
import { renderQtyStepper, updateStepperUI } from './components/qty-stepper';
import { renderCartBar, refreshCartBar } from './components/cart-bar';
import { renderOrderSheet, getOrderSheetL, renderLocationContainerContent } from './components/order-sheet';
import { renderOrderHistorySheet, type RestaurantOrderRecord } from './components/order-history-sheet';
import { renderActiveOrderTracker } from './components/active-order-tracker';
import { renderDishDetailModal } from './components/dish-detail-modal';
import { getActiveOutlet } from './reservation-view';
import type { CartItem, DietTag, OrderMode, PaymentMode, MenuCategory } from './types';
import { SERVICE_CHARGE_PCT } from './types';

const CART_SESSION_KEY = 'revio_menu_cart';
const STORAGE_ORDER_IDS_KEY = '_revio_restaurant_orders';
const STORAGE_ROOM_KEY = '_guest_room_number';
const STORAGE_TABLE_KEY = '_guest_table_number';

// ── Module-level state ──────────────────────────────────────────────────────
let activeMenuCategories: MenuCategory[] = [];
const menuCache = new Map<string, MenuCategory[]>();
let isLoadingMenu = false;
let cart = new Map<string, CartItem>();
let activeFilters = new Set<DietTag>();
let activeCatId = '';
let orderMode: OrderMode = 'room';
let payMode: PaymentMode = 'room';
let orderSheetOpen = false;
let orderSuccess = false;
let currentLoadedOutletId: string | null = null;
let activeDetailItemId: string | null = null;

let savedRoomNumber: string = '';
let savedTableNumber: string = '';
let isSubmittingOrder = false;

let orderHistorySheetOpen = false;
let orderHistoryList: RestaurantOrderRecord[] = [];
let isLoadingOrderHistory = false;

let isTableParamPresent = false;
let isRestaurantTenant = false;
let tenantFnbSettings: any = null;
let orderTrackerInterval: ReturnType<typeof setInterval> | null = null;

// ── Storage helpers ─────────────────────────────────────────────────────────
function initStoredLocations(): void {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const tableParam = urlParams.get('table')
      || sessionStorage.getItem('_guest_table_number')
      || localStorage.getItem(STORAGE_TABLE_KEY)
      || '';

    if (tableParam) {
      savedTableNumber = tableParam.trim();
      isTableParamPresent = true;
      orderMode = 'dine';
      payMode = 'tbl';
      localStorage.setItem(STORAGE_TABLE_KEY, savedTableNumber);
      savedRoomNumber = localStorage.getItem(STORAGE_ROOM_KEY) || '';
    } else {
      savedTableNumber = '';
      savedRoomNumber = localStorage.getItem(STORAGE_ROOM_KEY) || '';
    }
  } catch (_) {}
}

function getStoredOrderIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_ORDER_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function addStoredOrderId(id: string): void {
  try {
    const current = getStoredOrderIds();
    const updated = [id, ...current.filter((x) => x !== id)].slice(0, 30);
    localStorage.setItem(STORAGE_ORDER_IDS_KEY, JSON.stringify(updated));
  } catch (_) {}
}

// Restore cart from sessionStorage across soft reloads
function loadCartFromSession(): void {
  try {
    const raw = sessionStorage.getItem(CART_SESSION_KEY);
    if (!raw) return;
    const arr: Array<{ itemId: string; qty: number; note: string }> = JSON.parse(raw);
    cart.clear();
    for (const entry of arr) {
      const item = activeMenuCategories.flatMap((c) => c.items).find((i) => i.id === entry.itemId);
      if (item) cart.set(item.id, { item, qty: entry.qty, note: entry.note, selectedOptions: {} });
    }
  } catch (_) {}
}

function saveCartToSession(): void {
  try {
    const arr = [...cart.values()].map((ci) => ({ itemId: ci.item.id, qty: ci.qty, note: ci.note }));
    sessionStorage.setItem(CART_SESSION_KEY, JSON.stringify(arr));
  } catch (_) {}
}

/**
 * Fetch and load real menu categories for an outlet from Firestore API
 */
export async function loadMenuForOutlet(outletId: string): Promise<void> {
  if (!outletId) return;

  if (menuCache.has(outletId)) {
    activeMenuCategories = menuCache.get(outletId) || [];
    currentLoadedOutletId = outletId;
    isLoadingMenu = false;
    activeCatId = activeMenuCategories[0]?.id || '';
    rerenderSectionsAndTabs();
    return;
  }

  currentLoadedOutletId = outletId;
  isLoadingMenu = true;
  rerenderSectionsAndTabs();

  try {
    const res = await getRestaurantMenu(outletId);
    if (res && Array.isArray(res.categories)) {
      activeMenuCategories = res.categories;
      menuCache.set(outletId, res.categories);
    } else {
      activeMenuCategories = [];
      menuCache.set(outletId, []);
    }
  } catch (error) {
    console.warn('[Menu] Failed to fetch menu for outlet:', outletId, error);
    activeMenuCategories = [];
  } finally {
    isLoadingMenu = false;
    activeCatId = activeMenuCategories[0]?.id || '';
    rerenderSectionsAndTabs();
  }
}

// ── Render ──────────────────────────────────────────────────────────────────
export function renderMenuView(): string {
  initStoredLocations();
  loadCartFromSession();

  const cats = activeMenuCategories.filter((c) => c.active);
  const filterHtml = cats.length > 0 ? renderDietFilter(activeFilters) : '';
  const tabsHtml = cats.length > 0 ? renderCategoryTabs(cats, activeCatId, activeFilters) : '';

  let bodyHtml = '';
  if (isLoadingMenu) {
    bodyHtml = `
      <div style="padding:48px 20px;text-align:center;color:#888;">
        <div style="display:inline-block;width:32px;height:32px;border:3px solid #eee;border-top-color:#00B900;border-radius:50%;animation:spin 1s linear infinite;"></div>
        <p style="margin-top:12px;font-size:14px;color:#666;">${t('menuLoading')}</p>
      </div>
    `;
  } else if (cats.length === 0 || !cats.some((c) => c.items.length > 0)) {
    bodyHtml = `
      <div style="padding:60px 20px;text-align:center;color:#999;">
        <div style="font-size:48px;margin-bottom:12px;">🍽️</div>
        <p style="font-size:16px;font-weight:600;color:#333;margin-bottom:4px;">${t('menuEmptyTitle')}</p>
        <p style="font-size:13px;color:#888;">${t('menuEmptySubtitle')}</p>
      </div>
    `;
  } else {
    bodyHtml = cats
      .sort((a, b) => a.sort - b.sort)
      .map((cat) => renderMenuSection(cat, cart, activeFilters))
      .join('');
  }

  return `
    <div style="display:flex;justify-content:flex-end;padding:8px 16px 4px;">
      <button onclick="window.viewOrderHistory?.()" style="
        display:inline-flex;align-items:center;gap:6px;padding:6px 14px;background:#f8fafc;border:1.5px solid #e2e8f0;
        border-radius:20px;font-size:12px;font-weight:600;color:#334155;cursor:pointer;box-shadow:0 1px 3px rgba(0,0,0,0.04);touch-action:manipulation;">
        <span>📋</span> <span>${t('orderHistoryTitle')}</span>
      </button>
    </div>
    <div id="diet-filter-mount">${filterHtml}</div>
    <div id="menu-tabs-mount" style="position:sticky;top:var(--menu-tabs-top, 56px);z-index:80;background:#fff;">${tabsHtml}</div>
    <div id="menu-sections" style="padding-bottom:80px;">
      ${bodyHtml}
    </div>
    <div id="cart-bar-mount">${renderCartBar(cart)}</div>
    <div id="active-order-tracker-mount">${renderActiveOrderTracker(orderHistoryList, cart.size > 0, savedTableNumber)}</div>
  `;
}

// ── Init (handlers) ─────────────────────────────────────────────────────────
export function initMenuView(outletId?: string): void {
  initStoredLocations();
  loadCartFromSession();
  loadOrderHistorySilently();

  if (orderTrackerInterval) {
    clearInterval(orderTrackerInterval);
  }
  orderTrackerInterval = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      loadOrderHistorySilently();
    }
  }, 15000);

  const updateTabsTop = () => {
    const hasServiceTabs = Boolean(document.querySelector('.nav-service-tab'));
    const tabsTop = hasServiceTabs ? '101px' : '56px';
    document.documentElement.style.setProperty('--menu-tabs-top', tabsTop);
  };
  updateTabsTop();

  // Fetch tenant info to detect standalone restaurant vs hotel
  getHotelInfo().then((hotelInfo) => {
    if (!hotelInfo) return;
    isRestaurantTenant = hotelInfo.isRestaurant === true || hotelInfo.industry === 'restaurant';
    tenantFnbSettings = hotelInfo.fnbSettings || null;
    if (isRestaurantTenant || tenantFnbSettings?.orderModes?.roomDelivery === false) {
      if (orderMode === 'room') orderMode = 'dine';
      if (payMode === 'room') payMode = 'tbl';
    }
  }).catch(() => null);

  // If outletId provided and not loaded yet, fetch dynamic menu from API
  if (outletId) {
    if (outletId !== currentLoadedOutletId || (activeMenuCategories.length === 0 && !isLoadingMenu)) {
      loadMenuForOutlet(outletId);
    }
  }

  // Default active category to first visible
  const firstVisible = activeMenuCategories.find(
    (c) => c.active && c.items.some((i) => itemMatchesFilters(i.tags, activeFilters) && i.available)
  );
  if (firstVisible && !activeCatId) activeCatId = firstVisible.id;

  initScrollSpy((catId) => {
    activeCatId = catId;
    setActiveTabUI(catId);
  });

  // Tab click
  (window as any).onCatTabClick = (catId: string) => {
    activeCatId = catId;
    handleCatTabClick(catId);
  };

  // Diet filter toggle
  (window as any).toggleDietFilter = (tag: DietTag) => {
    if (activeFilters.has(tag)) activeFilters.delete(tag);
    else activeFilters.add(tag);
    updateDietChipUI(tag, activeFilters.has(tag));
    rerenderSectionsAndTabs();
  };

  // Add item to cart
  (window as any).addToCart = (itemId: string) => {
    let item = activeMenuCategories.flatMap((c) => c.items).find((i) => i.id === itemId);
    if (!item) item = cart.get(itemId)?.item;
    if (!item || !item.available) return;
    const existing = cart.get(itemId);
    if (existing) {
      existing.qty += 1;
    } else {
      cart.set(itemId, { item, qty: 1, note: '', selectedOptions: {} });
    }

    saveCartToSession();
    const qty = cart.get(itemId)!.qty;
    updateStepperUI(itemId, qty);
    rerenderItemCard(itemId, qty);
    refreshCartBarMount();
    if (activeDetailItemId === itemId) {
      const modalStepper = document.getElementById('modal-stepper-mount');
      if (modalStepper) modalStepper.innerHTML = renderQtyStepper(itemId, qty);
    }
    updateOrderSheetItemUI(itemId, qty);
  };

  // Decrease / remove item from cart
  (window as any).decreaseCart = (itemId: string) => {
    const existing = cart.get(itemId);
    if (!existing) return;
    existing.qty -= 1;
    if (existing.qty <= 0) cart.delete(itemId);
    saveCartToSession();
    const qty = cart.get(itemId)?.qty ?? 0;
    updateStepperUI(itemId, qty);
    rerenderItemCard(itemId, qty);
    refreshCartBarMount();
    if (activeDetailItemId === itemId) {
      const modalStepper = document.getElementById('modal-stepper-mount');
      if (modalStepper) modalStepper.innerHTML = renderQtyStepper(itemId, qty);
    }
    updateOrderSheetItemUI(itemId, qty);
  };

  // Dish detail modal (Lightbox)
  (window as any).openDishDetail = (itemId: string) => {
    mountDishDetail(itemId);
  };

  (window as any).closeDishDetail = () => {
    closeDishDetail();
  };

  // Cart bar open
  (window as any).openOrderSheet = () => {
    orderSheetOpen = true;
    orderSuccess = false;
    mountOrderSheet();
    const backToTopBtn = document.querySelector<HTMLElement>('[id$="-back-to-top"]');
    if (backToTopBtn) backToTopBtn.style.visibility = 'hidden';
  };

  (window as any).closeOrderSheet = () => {
    orderSheetOpen = false;
    orderSuccess = false;
    document.getElementById('order-sheet-mount')?.remove();
    const backToTopBtn = document.querySelector<HTMLElement>('[id$="-back-to-top"]');
    if (backToTopBtn) backToTopBtn.style.visibility = '';
    refreshCartBarMount();
  };

  (window as any).setOrderMode = (m: OrderMode) => {
    orderMode = m;
    // Align payment mode to match order mode
    const newPayMode: PaymentMode = m === 'room' ? 'room' : 'tbl';
    payMode = newPayMode;

    // In-place button UI updates (0ms, zero blink)
    updateModeBtnUI('sheet-mode-dine', m === 'dine');
    updateModeBtnUI('sheet-mode-takeaway', m === 'takeaway');
    updateModeBtnUI('sheet-mode-room', m === 'room');

    // In-place Location Container update
    const locContainer = document.getElementById('sheet-location-container');
    if (locContainer) {
      const isLocked = isTableParamPresent && Boolean(savedTableNumber);
      locContainer.innerHTML = renderLocationContainerContent(m, savedRoomNumber, savedTableNumber, isLocked);
    }

    // In-place Pay Mode buttons update
    updateModeBtnUI('sheet-pay-tbl', payMode === 'tbl');
    updateModeBtnUI('sheet-pay-room', payMode === 'room');
  };

  (window as any).setPayMode = (m: PaymentMode) => {
    payMode = m;
    updateModeBtnUI('sheet-pay-tbl', m === 'tbl');
    updateModeBtnUI('sheet-pay-room', m === 'room');
  };

  (window as any).setOrderLocation = (val: string) => {
    if (orderMode === 'room') {
      savedRoomNumber = val;
      try {
        localStorage.setItem(STORAGE_ROOM_KEY, val);
      } catch (_) {}
    } else if (orderMode === 'dine') {
      savedTableNumber = val;
      try {
        localStorage.setItem(STORAGE_TABLE_KEY, val);
      } catch (_) {}
    }
  };

  // Submit order to API
  (window as any).submitOrder = async () => {
    if (isSubmittingOrder) return;
    if (cart.size === 0) return;

    let locValue = '';
    if (orderMode === 'room') {
      locValue = savedRoomNumber.trim();
      if (!locValue) {
        alert(t('roomNumberRequired'));
        document.getElementById('sheet-location-input')?.focus();
        return;
      }
    } else if (orderMode === 'dine') {
      locValue = savedTableNumber.trim();
      if (!locValue) {
        alert(t('tableNumberRequired'));
        document.getElementById('sheet-location-input')?.focus();
        return;
      }
    } else {
      locValue = 'takeaway';
    }

    const submitBtn = document.getElementById('sheet-submit-btn') as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
      submitBtn.style.opacity = '0.7';
    }

    isSubmittingOrder = true;

    try {
      const items = [...cart.values()].map((ci) => ({
        id: ci.item.id,
        name: ci.item.name,
        price: ci.item.price,
        qty: ci.qty,
        notes: ci.note || undefined,
        options: ci.selectedOptions,
      }));

      const subtotal = [...cart.values()].reduce((s, c) => s + c.item.price * c.qty, 0);
      const svcRate = tenantFnbSettings?.serviceChargePct ?? SERVICE_CHARGE_PCT;
      const svc = Math.round((subtotal * svcRate) / 100);
      const total = subtotal + svc;

      const activeOutlet = getActiveOutlet();
      const outletName = activeOutlet?.name || undefined;

      const payload: SubmitRestaurantOrderPayload = {
        orderMode,
        payMode,
        paymentMode: payMode,
        roomNumber: orderMode === 'room' ? locValue : undefined,
        tableNumber: orderMode === 'dine' ? locValue : undefined,
        outletId: currentLoadedOutletId || activeOutlet?.id || undefined,
        outletName,
        items,
        subtotal,
        serviceCharge: svc,
        total,
        currency: 'THB',
      };

      const res = await submitRestaurantOrder(payload);
      if (res && res.success && res.orderId) {
        addStoredOrderId(res.orderId);
        orderSuccess = true;
        cart.clear();
        saveCartToSession();
        refreshCartBarMount();
        loadOrderHistorySilently();

        // Reset all steppers on menu page
        document.querySelectorAll<HTMLElement>('.menu-stepper').forEach((el) => {
          const card = el.closest<HTMLElement>('.menu-item-card');
          const itId = card?.getAttribute('data-item-id');
          if (itId) {
            const wrapper = document.createElement('div');
            wrapper.innerHTML = renderQtyStepper(itId, 0);
            const newStepper = wrapper.firstElementChild;
            if (newStepper) el.replaceWith(newStepper);
          }
        });
        if (activeDetailItemId) {
          const modalStepper = document.getElementById('modal-stepper-mount');
          if (modalStepper) modalStepper.innerHTML = renderQtyStepper(activeDetailItemId, 0);
        }

        refreshOrderSheet();
        refreshCartBarMount();
      } else {
        throw new Error(res?.error || 'Submit order failed');
      }
    } catch (err: any) {
      console.error('[Menu] Order submission failed:', err);
      alert(err?.message || 'Failed to place order. Please try again.');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = getOrderSheetL('place');
        submitBtn.style.opacity = '1';
      }
    } finally {
      isSubmittingOrder = false;
    }
  };

  // Order history handlers
  (window as any).viewOrderHistory = () => {
    (window as any).closeOrderSheet?.();
    openOrderHistorySheet();
  };

  (window as any).closeOrderHistory = () => {
    closeOrderHistorySheet();
  };

  (window as any).closeOrderHistorySheet = () => {
    closeOrderHistorySheet();
  };

  (window as any).refreshOrderHistory = () => {
    loadOrderHistory();
  };
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function rerenderSectionsAndTabs(): void {
  const cats = activeMenuCategories.filter((c) => c.active);

  const filterMount = document.getElementById('diet-filter-mount');
  if (filterMount) {
    filterMount.innerHTML = cats.length > 0 ? renderDietFilter(activeFilters) : '';
  }

  const tabsMount = document.getElementById('menu-tabs-mount');
  if (tabsMount) {
    tabsMount.innerHTML = cats.length > 0 ? renderCategoryTabs(cats, activeCatId, activeFilters) : '';
  }

  const sectionsEl = document.getElementById('menu-sections');
  if (sectionsEl) {
    if (isLoadingMenu) {
      sectionsEl.innerHTML = `
        <div style="padding:48px 20px;text-align:center;color:#888;">
          <div style="display:inline-block;width:32px;height:32px;border:3px solid #eee;border-top-color:#00B900;border-radius:50%;animation:spin 1s linear infinite;"></div>
          <p style="margin-top:12px;font-size:14px;color:#666;">${t('menuLoading')}</p>
        </div>
      `;
    } else if (cats.length === 0 || !cats.some((c) => c.items.length > 0)) {
      sectionsEl.innerHTML = `
        <div style="padding:60px 20px;text-align:center;color:#999;">
          <div style="font-size:48px;margin-bottom:12px;">🍽️</div>
          <p style="font-size:16px;font-weight:600;color:#333;margin-bottom:4px;">${t('menuEmptyTitle')}</p>
          <p style="font-size:13px;color:#888;">${t('menuEmptySubtitle')}</p>
        </div>
      `;
    } else {
      sectionsEl.innerHTML = cats
        .sort((a, b) => a.sort - b.sort)
        .map((cat) => renderMenuSection(cat, cart, activeFilters))
        .join('');
      // Re-init scroll spy on new DOM
      initScrollSpy((catId) => { activeCatId = catId; setActiveTabUI(catId); });
    }
  }
}

function rerenderItemCard(itemId: string, qty: number): void {
  // Re-render only the stepper portion of the card (within #menu-sections)
  const card = document.querySelector<HTMLElement>(`.menu-item-card[data-item-id="${itemId}"]`);
  if (!card) return;
  const stepperEl = card.querySelector<HTMLElement>('.menu-stepper, .menu-add-btn');
  if (!stepperEl) return;
  const wrapper = document.createElement('div');
  wrapper.innerHTML = renderQtyStepper(itemId, qty);
  const newStepper = wrapper.firstElementChild;
  if (newStepper) stepperEl.replaceWith(newStepper);
}

function refreshCartBarMount(): void {
  const mount = document.getElementById('cart-bar-mount');
  if (mount) mount.innerHTML = renderCartBar(cart);
  refreshActiveOrderTrackerMount();
}

function refreshActiveOrderTrackerMount(): void {
  const trackerMount = document.getElementById('active-order-tracker-mount');
  if (trackerMount) trackerMount.innerHTML = renderActiveOrderTracker(orderHistoryList, cart.size > 0, savedTableNumber);
}

function updateModeBtnUI(btnId: string, active: boolean): void {
  const btn = document.getElementById(btnId);
  if (!btn) return;
  btn.style.border = `1.5px solid ${active ? '#00B900' : '#e0e0e0'}`;
  btn.style.background = active ? '#E8F5E8' : '#fff';
  btn.style.color = active ? '#00B900' : '#555';
  btn.style.fontWeight = active ? '700' : '500';
}

function updateOrderSheetTotals(): void {
  if (!orderSheetOpen) return;
  const subtotal = [...cart.values()].reduce((s, c) => s + c.item.price * c.qty, 0);
  const svc = Math.round((subtotal * SERVICE_CHARGE_PCT) / 100);
  const total = subtotal + svc;

  const subtotalEl = document.getElementById('sheet-subtotal');
  if (subtotalEl) subtotalEl.textContent = `฿${subtotal.toLocaleString()}`;

  const svcEl = document.getElementById('sheet-svc');
  if (svcEl) svcEl.textContent = `฿${svc.toLocaleString()}`;

  const totalEl = document.getElementById('sheet-total');
  if (totalEl) totalEl.textContent = `฿${total.toLocaleString()}`;
}

function updateOrderSheetItemUI(itemId: string, qty: number): void {
  if (!orderSheetOpen) return;
  const safeId = itemId.replace(/[^a-zA-Z0-9]/g, '_');
  const rowEl = document.getElementById(`sheet-row-${safeId}`);
  const qtyEl = document.getElementById(`sheet-qty-${safeId}`);
  const priceEl = document.getElementById(`sheet-price-${safeId}`);

  if (qty <= 0) {
    if (rowEl) rowEl.remove();
    if (cart.size === 0) {
      (window as any).closeOrderSheet?.();
      return;
    }
  } else {
    const ci = cart.get(itemId);
    if (ci) {
      if (qtyEl) qtyEl.textContent = String(qty);
      if (priceEl) priceEl.textContent = `฿${(ci.item.price * qty).toLocaleString()}`;
    }
  }
  updateOrderSheetTotals();
}

function mountOrderSheet(): void {
  let mount = document.getElementById('order-sheet-mount');
  if (!mount) {
    mount = document.createElement('div');
    mount.id = 'order-sheet-mount';
    document.body.appendChild(mount);
  }
  const sheetOptions = {
    allowRoomDelivery: tenantFnbSettings?.orderModes?.roomDelivery ?? (!isRestaurantTenant),
    allowDineIn: tenantFnbSettings?.orderModes?.dineIn ?? true,
    allowTakeaway: tenantFnbSettings?.orderModes?.takeaway ?? true,
    allowChargeToRoom: tenantFnbSettings?.paymentModes?.chargeToRoom ?? (!isRestaurantTenant),
    allowPayAtTable: tenantFnbSettings?.paymentModes?.payAtTable ?? true,
    isTableLocked: isTableParamPresent && Boolean(savedTableNumber),
    serviceChargePct: tenantFnbSettings?.serviceChargePct ?? SERVICE_CHARGE_PCT,
  };
  mount.innerHTML = renderOrderSheet(
    cart,
    orderMode,
    payMode,
    orderSuccess,
    savedRoomNumber,
    savedTableNumber,
    sheetOptions
  );
}

function refreshOrderSheet(): void {
  if (!orderSheetOpen) return;
  mountOrderSheet();
}

function mountDishDetail(itemId: string): void {
  const item = activeMenuCategories.flatMap((c) => c.items).find((i) => i.id === itemId);
  if (!item) return;
  activeDetailItemId = itemId;
  let mount = document.getElementById('dish-detail-mount');
  if (!mount) {
    mount = document.createElement('div');
    mount.id = 'dish-detail-mount';
    document.body.appendChild(mount);
  }
  const qty = cart.get(itemId)?.qty ?? 0;
  mount.innerHTML = renderDishDetailModal(item, qty);
}

function closeDishDetail(): void {
  activeDetailItemId = null;
  document.getElementById('dish-detail-mount')?.remove();
}

// ── Order History modal ─────────────────────────────────────────────────────
function openOrderHistorySheet(): void {
  orderHistorySheetOpen = true;
  mountOrderHistorySheet();
  const backToTopBtn = document.querySelector<HTMLElement>('[id$="-back-to-top"]');
  if (backToTopBtn) backToTopBtn.style.visibility = 'hidden';
  loadOrderHistory();
}

function closeOrderHistorySheet(): void {
  orderHistorySheetOpen = false;
  document.getElementById('order-history-sheet-mount')?.remove();
  const backToTopBtn = document.querySelector<HTMLElement>('[id$="-back-to-top"]');
  if (backToTopBtn) backToTopBtn.style.visibility = '';
}

async function queryActiveOrders(): Promise<any[]> {
  const storedIds = getStoredOrderIds();
  const room = savedRoomNumber.trim();
  const table = savedTableNumber.trim();

  // If table is present, query by tableNumber first
  if (table) {
    const orders = await fetchRestaurantOrders(undefined, undefined, table);
    if (Array.isArray(orders) && orders.length > 0) return orders;
    // Fallback: if no table orders found yet but stored order IDs exist, check them
    if (storedIds.length > 0) {
      const fallbackOrders = await fetchRestaurantOrders(storedIds);
      if (Array.isArray(fallbackOrders) && fallbackOrders.length > 0) return fallbackOrders;
    }
    return Array.isArray(orders) ? orders : [];
  }

  // If room is present, query by roomNumber
  if (room) {
    const orders = await fetchRestaurantOrders(undefined, room);
    if (Array.isArray(orders) && orders.length > 0) return orders;
    if (storedIds.length > 0) {
      const fallbackOrders = await fetchRestaurantOrders(storedIds);
      if (Array.isArray(fallbackOrders) && fallbackOrders.length > 0) return fallbackOrders;
    }
    return Array.isArray(orders) ? orders : [];
  }

  // Otherwise query by storedIds
  if (storedIds.length > 0) {
    return await fetchRestaurantOrders(storedIds);
  }

  return [];
}

async function loadOrderHistory(): Promise<void> {
  const storedIds = getStoredOrderIds();
  const room = savedRoomNumber.trim();
  const table = savedTableNumber.trim();
  if (storedIds.length === 0 && !room && !table) {
    orderHistoryList = [];
    isLoadingOrderHistory = false;
    refreshOrderHistoryMount();
    return;
  }

  isLoadingOrderHistory = true;
  refreshOrderHistoryMount();

  try {
    const orders = await queryActiveOrders();
    if (Array.isArray(orders)) {
      orderHistoryList = orders;
      refreshActiveOrderTrackerMount();
    }
  } catch (err) {
    console.warn('[Menu] Failed to load order history:', err);
  } finally {
    isLoadingOrderHistory = false;
    refreshOrderHistoryMount();
  }
}

async function loadOrderHistorySilently(): Promise<void> {
  const storedIds = getStoredOrderIds();
  const room = savedRoomNumber.trim();
  const table = savedTableNumber.trim();
  if (storedIds.length === 0 && !room && !table) return;
  try {
    const orders = await queryActiveOrders();
    if (Array.isArray(orders)) {
      orderHistoryList = orders;
      refreshActiveOrderTrackerMount();
    }
  } catch (err) {
    console.warn('[Menu] Failed to load order history silently:', err);
  }
}

function mountOrderHistorySheet(): void {
  let mount = document.getElementById('order-history-sheet-mount');
  if (!mount) {
    mount = document.createElement('div');
    mount.id = 'order-history-sheet-mount';
    document.body.appendChild(mount);
  }
  mount.innerHTML = renderOrderHistorySheet(orderHistoryList, isLoadingOrderHistory);
}

function refreshOrderHistoryMount(): void {
  if (!orderHistorySheetOpen) return;
  mountOrderHistorySheet();
}

export function cleanupMenuView(): void {
  if (orderTrackerInterval) {
    clearInterval(orderTrackerInterval);
    orderTrackerInterval = null;
  }
  destroyScrollSpy();
  closeDishDetail();
  closeOrderHistorySheet();
  document.getElementById('active-order-tracker-mount')?.remove();
}
